// ============================================================
// EntityJoinDiagram.jsx
//
// A branded, responsive architectural visualization page for
// "Entity Join Mapping: User → Post".
//
// Author: ProDev Enterprise Series (Java Branch)
// Features: Relational schema visualization, PK/FK mapping,
//           and navigation bridge to the Dashboard.
// ============================================================

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

// ── MUI Layout & Display ──────────────────────────────────
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Tooltip,
    Paper,
    Fab,
    Zoom,
    Stack,
    Avatar,
    Button
} from "@mui/material";

// ── MUI Icons ────────────────────────────────────────────
import AccountTreeIcon        from "@mui/icons-material/AccountTree";
import StorageIcon            from "@mui/icons-material/Storage";
import SettingsEthernetIcon   from "@mui/icons-material/SettingsEthernet";
import TerminalIcon           from "@mui/icons-material/Terminal";
import LightbulbIcon          from "@mui/icons-material/Lightbulb";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon        from "@mui/icons-material/ContentCopy";
import DoneIcon               from "@mui/icons-material/Done";
import SchoolIcon             from "@mui/icons-material/School";
import StarIcon               from "@mui/icons-material/Star";
import KeyboardArrowUpIcon    from "@mui/icons-material/KeyboardArrowUp";
import ArrowBackIcon          from "@mui/icons-material/ArrowBack";
import CodeIcon               from "@mui/icons-material/Code";
import SchemaIcon             from "@mui/icons-material/Schema";

// ── Brand tokens ──────────────────────────────────────────
const BRAND = {
    bg:          "#233D4D",
    surface:     "rgba(255,255,255,0.04)",
    border:      "rgba(218,165,32,0.25)",
    borderHover: "rgba(218,165,32,0.6)",
    gold:        "#DAA520",
    goldBright:  "#FFD700",
    goldGlow:    "rgba(218,165,32,0.15)",
    white:       "#FFFFFF",
    muted:       "rgba(255,255,255,0.55)",
    codeBg:      "rgba(0,0,0,0.45)",
    codeText:    "#FFD700",
};

const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
    letterSpacing: "-0.5px",
};

// ── Sub-Components ────────────────────────────────────────

function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box sx={{
                width: 38, height: 38, borderRadius: "50%",
                background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`,
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>
            <Typography variant="h6" sx={{ ...sectionHeading, fontSize: { xs: 17, md: 19 } }}>
                {label}
            </Typography>
        </Box>
    );
}

function CodeBlock({ title, code }) {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(code).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 2, overflow: "hidden", mb: 4 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${BRAND.border}`, background: "rgba(0,0,0,0.3)" }}>
                <Typography sx={{ color: BRAND.gold, fontSize: 11, fontWeight: 700, fontFamily: "monospace", textTransform: 'uppercase' }}>{title}</Typography>
                <Tooltip title={copied ? "Copied!" : "Copy code"} placement="top">
                    <Box onClick={handleCopy} sx={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 0.5, color: copied ? BRAND.goldBright : BRAND.muted, fontSize: 12 }}>
                        {copied ? <DoneIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
                        <span>{copied ? "Copied" : "Copy"}</span>
                    </Box>
                </Tooltip>
            </Box>
            <Box sx={{ p: 2.5 }}>
                <Typography component="pre" sx={{ fontFamily: "monospace", fontSize: 13, color: BRAND.codeText, whiteSpace: "pre-wrap", m: 0 }}>
                    {code}
                </Typography>
            </Box>
        </Box>
    );
}

// ── Main Component ────────────────────────────────────────

export default function EntityJoinDiagram() {
    const [showScroll, setShowScroll] = useState(false);

    useEffect(() => {
        const checkScroll = () => {
            if (window.pageYOffset > 400) setShowScroll(true);
            else setShowScroll(false);
        };
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, []);

    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 4, md: 7 }, px: { xs: 2, sm: 3 },
            backgroundImage: `repeating-linear-gradient(-45deg, rgba(218,165,32,0.02) 0px, rgba(218,165,32,0.02) 1px, transparent 1px, transparent 12px)`
        }}>
            <Container maxWidth="lg">
                {/* HERO HEADER */}
                <Box sx={{ mb: { xs: 5, md: 7 }, textAlign: { xs: "center", md: "left" } }}>
                    <Chip icon={<SchoolIcon sx={{ color: `${BRAND.gold} !important`, fontSize: 15 }} />} label="Java Enterprise · Module 11"
                          sx={{ mb: 2.5, background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`, color: BRAND.gold, fontFamily: "monospace", fontSize: 11 }} />
                    <Typography variant="h3" sx={{ ...sectionHeading, fontSize: { xs: 28, sm: 36, md: 44 }, mb: 2 }}>
                        Entity Join <span>Visualization</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 14, md: 16 }, maxWidth: 620, lineHeight: 1.75, mx: { xs: "auto", md: 0 } }}>
                        Visualize the standard <strong>One-to-Many</strong> mapping between a User and their Posts. This architectural blueprint demonstrates how Foreign Keys bridge the gap between Java objects and SQL tables.
                    </Typography>
                </Box>

                {/* 1. VISUAL DIAGRAM */}
                <Box sx={{ mb: 8 }}>
                    <SectionTitle icon={<AccountTreeIcon />} label="1. Relational Schema Blueprint" />
                    <Paper sx={{ p: { xs: 3, md: 5 }, background: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 3 }}>
                        <Grid container spacing={4} alignItems="center" justifyContent="center">
                            {/* User Entity */}
                            <Grid item xs={12} md={4}>
                                <Box sx={{ border: `2px solid ${BRAND.gold}`, borderRadius: 2, p: 2, textAlign: 'center', background: 'rgba(218,165,32,0.05)' }}>
                                    <Typography variant="h6" sx={{ color: BRAND.goldBright, fontWeight: 700, mb: 2 }}>User (Parent)</Typography>
                                    <Stack spacing={1} sx={{ color: BRAND.white, fontFamily: 'monospace', fontSize: 13 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><span>id</span><span style={{ color: BRAND.gold }}>[PK]</span></Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><span>username</span></Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><span>email</span></Box>
                                    </Stack>
                                </Box>
                            </Grid>

                            {/* Join Indicator */}
                            <Grid item xs={12} md={2} sx={{ textAlign: 'center' }}>
                                <Tooltip title="Mapped by 'user' property" arrow>
                                    <Box sx={{ color: BRAND.gold, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                        <Typography variant="caption" sx={{ mb: 1, fontWeight: 700 }}>1 : N</Typography>
                                        <SettingsEthernetIcon sx={{ fontSize: 40 }} />
                                        <Typography variant="caption" sx={{ mt: 1, fontFamily: 'monospace' }}>user_id</Typography>
                                    </Box>
                                </Tooltip>
                            </Grid>

                            {/* Post Entity */}
                            <Grid item xs={12} md={4}>
                                <Box sx={{ border: `2px solid ${BRAND.muted}`, borderRadius: 2, p: 2, textAlign: 'center', background: 'rgba(255,255,255,0.02)' }}>
                                    <Typography variant="h6" sx={{ color: BRAND.white, fontWeight: 700, mb: 2 }}>Post (Child)</Typography>
                                    <Stack spacing={1} sx={{ color: BRAND.white, fontFamily: 'monospace', fontSize: 13 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><span>id</span><span style={{ color: BRAND.muted }}>[PK]</span></Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><span>title</span></Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><span>content</span></Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', borderTop: `1px dashed ${BRAND.border}`, pt: 1 }}>
                                            <span style={{ color: BRAND.goldBright }}>user_id</span><span style={{ color: BRAND.goldBright }}>[FK]</span>
                                        </Box>
                                    </Stack>
                                </Box>
                            </Grid>
                        </Grid>
                    </Paper>
                </Box>

                {/* 2. JPA IMPLEMENTATION */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<CodeIcon />} label="2. JPA Mapping Implementation" />
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <CodeBlock
                                title="User.java (Parent)"
                                code={`@Entity\npublic class User {\n  @Id\n  private Long id;\n\n  @OneToMany(mappedBy = "user")\n  private List<Post> posts;\n}`}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <CodeBlock
                                title="Post.java (Child)"
                                code={`@Entity\npublic class Post {\n  @Id\n  private Long id;\n\n  @ManyToOne\n  @JoinColumn(name = "user_id")\n  private User user;\n}`}
                            />
                        </Grid>
                    </Grid>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* BEST PRACTICES */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<StarIcon />} label="Relational Mapping Highlights" />
                    <Grid container spacing={1.5}>
                        {[
                            "mappedBy: Informs JPA that Post owns the Foreign Key relationship",
                            "JoinColumn: Defines the physical database column (user_id) for the join",
                            "1 : N: A single parent record linked to multiple child records",
                            "Referential Integrity: user_id [FK] must point to a valid id [PK] in User",
                            "Consistency: Changes to User.id must propagate or block dependent Posts",
                            "Querying: Fetching a User can optionally load all associated Posts"
                        ].map((item) => (
                            <Grid item xs={12} sm={6} key={item}>
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.2 }}>
                                    <CheckCircleOutlineIcon sx={{ color: BRAND.gold, fontSize: 19, mt: 0.1 }} />
                                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>{item}</Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* FOOTER ACTIONS */}
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Button
                        component={Link}
                        to="/java-dashboard"
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        sx={{
                            borderColor: BRAND.gold,
                            color: BRAND.gold,
                            borderRadius: "99px",
                            px: 4,
                            fontWeight: 800,
                            "&:hover": {
                                borderColor: BRAND.goldBright,
                                background: BRAND.goldGlow
                            }
                        }}
                    >
                        Return to Hub
                    </Button>
                </Box>

                <Box sx={{ mt: 2, p: 2.5, border: `1px solid ${BRAND.border}`, borderRadius: 2, background: BRAND.goldGlow, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <LightbulbIcon sx={{ color: BRAND.gold, flexShrink: 0 }} />
                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>
                        <strong style={{ color: BRAND.white }}>Pro Tip:</strong> Always place the <code>@JoinColumn</code> on the <strong>owning side</strong> (usually the "Many" side) of the relationship. This ensures that the database schema is correctly generated with the Foreign Key on the Child table.
                    </Typography>
                </Box>
            </Container>

            {/* ✅ Floating Scroll-to-Top */}
            <Stack spacing={2} sx={{ position: "fixed", bottom: 25, right: 25, zIndex: 1000 }}>
                <Zoom in={showScroll}>
                    <Fab size="small" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} sx={{ bgcolor: BRAND.gold, "&:hover": { bgcolor: BRAND.goldBright } }}>
                        <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                    </Fab>
                </Zoom>
            </Stack>
        </Box>
    );
}
